import i18n, { DEFAULT_LANGUAGE, t } from "i18n";

// `label` is a getter so it is translated at read time, not at module load.
const pos = (id) => ({
  id,
  get label() {
    return t(`utils.positions.${id}`);
  },
});

export const POSITIONS_BY_SPORT = {
  rugby: [
    // General positions (for players who play multiple)
    pos("forward"),
    pos("back"),
    // Specific positions
    pos("loosehead-prop"),
    pos("hooker"),
    pos("tighthead-prop"),
    pos("lock"),
    pos("blindside-flanker"),
    pos("openside-flanker"),
    pos("number-8"),
    pos("scrum-half"),
    pos("fly-half"),
    pos("inside-centre"),
    pos("outside-centre"),
    pos("left-wing"),
    pos("right-wing"),
    pos("fullback"),
  ],
  soccer: [
    // General positions
    pos("goalkeeper"),
    pos("defender"),
    pos("midfielder"),
    pos("attacker"),
    // Specific positions
    pos("right-back"),
    pos("left-back"),
    pos("centre-back"),
    pos("defensive-midfielder"),
    pos("central-midfielder"),
    pos("attacking-midfielder"),
    pos("right-winger"),
    pos("left-winger"),
    pos("striker"),
  ],
  basketball: [
    // General positions
    pos("guard"),
    pos("forward"),
    // Specific positions
    pos("point-guard"),
    pos("shooting-guard"),
    pos("small-forward"),
    pos("power-forward"),
    pos("center"),
  ],
  "gaelic football": [
    // General positions
    pos("goalkeeper"),
    pos("defender"),
    pos("midfielder"),
    pos("forward"),
    // Specific positions
    pos("right-corner-back"),
    pos("full-back"),
    pos("left-corner-back"),
    pos("right-half-back"),
    pos("centre-half-back"),
    pos("left-half-back"),
    pos("midfield"),
    pos("right-half-forward"),
    pos("centre-half-forward"),
    pos("left-half-forward"),
    pos("right-corner-forward"),
    pos("full-forward"),
    pos("left-corner-forward"),
  ],
  hurling: [
    // General positions
    pos("goalkeeper"),
    pos("defender"),
    pos("midfielder"),
    pos("forward"),
    // Specific positions
    pos("right-corner-back"),
    pos("full-back"),
    pos("left-corner-back"),
    pos("right-half-back"),
    pos("centre-half-back"),
    pos("left-half-back"),
    pos("midfield"),
    pos("right-half-forward"),
    pos("centre-half-forward"),
    pos("left-half-forward"),
    pos("right-corner-forward"),
    pos("full-forward"),
    pos("left-corner-forward"),
  ],
  camogie: [
    // General positions (same as hurling)
    pos("goalkeeper"),
    pos("defender"),
    pos("midfielder"),
    pos("forward"),
    // Specific positions
    pos("right-corner-back"),
    pos("full-back"),
    pos("left-corner-back"),
    pos("right-half-back"),
    pos("centre-half-back"),
    pos("left-half-back"),
    pos("midfield"),
    pos("right-half-forward"),
    pos("centre-half-forward"),
    pos("left-half-forward"),
    pos("right-corner-forward"),
    pos("full-forward"),
    pos("left-corner-forward"),
  ],
  hockey: [
    // General positions
    pos("goalkeeper"),
    pos("defender"),
    pos("midfielder"),
    pos("forward"),
    // Specific positions
    pos("right-back"),
    pos("left-back"),
    pos("centre-back"),
    pos("right-half"),
    pos("centre-half"),
    pos("left-half"),
    pos("right-wing"),
    pos("centre-forward"),
    pos("left-wing"),
    pos("inside-right"),
    pos("inside-left"),
  ],
  sailing: [
    pos("skipper"),
    pos("helmsman"),
    pos("tactician"),
    pos("navigator"),
    pos("trimmer"),
    pos("bowman"),
    pos("grinder"),
    pos("pitman"),
    pos("crew"),
  ],
};

// Helper to get positions for a sport (case-insensitive, falls back to empty array)
export const getPositionsForSport = (sport) => {
  if (!sport) return [];
  return POSITIONS_BY_SPORT[sport.toLowerCase()] || [];
};

// Positions are stored on the player as free text. Known positions are saved
// in English (so data is the same whatever language the coach uses) and shown
// in the current language; anything a user typed themselves is left as-is.
const ALL_POSITION_IDS = [
  ...new Set(
    Object.values(POSITIONS_BY_SPORT).flatMap((list) =>
      list.map((position) => position.id)
    )
  ),
];

const englishLabel = (id) =>
  i18n.t(`utils.positions.${id}`, { lng: DEFAULT_LANGUAGE });

// Some translations share a label across sports (e.g. French "Ailier droit"
// is both soccer's Right Winger and rugby/hockey's Right Wing), so the team's
// own sport is searched first.
const findPositionId = (text, labelFor, sport) => {
  const needle = (text || "").trim().toLowerCase();
  if (!needle) return null;
  const matches = (id) => labelFor(id).toLowerCase() === needle;
  const sportIds = getPositionsForSport(sport).map((position) => position.id);
  return sportIds.find(matches) || ALL_POSITION_IDS.find(matches) || null;
};

// Value to send to the API for what the user entered in the position field.
export const positionToApi = (text, sport) => {
  const id = findPositionId(
    text,
    (positionId) => t(`utils.positions.${positionId}`),
    sport
  );
  return id ? englishLabel(id) : text;
};

// Display text for a position stored on a player.
export const positionLabel = (stored) => {
  const id = findPositionId(stored, englishLabel);
  return id ? t(`utils.positions.${id}`) : stored;
};
